import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import CostCenterGroupsSettings from "../../dp/components/CostCenterGroupsSettings";
import {
  createCostCenterApi,
  deleteCostCenterApi,
  DpCostCenter,
  DpCostCenterGroup,
  listCostCenterGroupsApi,
  listCostCentersApi,
  updateCostCenterApi,
} from "../../dp/api/settingsApi";
import { listStoresApi } from "../api/financeiroApi";
import type { FinStore } from "../api/finTypes";

type Props = { token: string; canWrite: boolean };

const BASE = "/financeiro/settings" as const;
const emptyForm = { code: "", name: "", storeIds: [] as string[], isActive: true, groupId: "" };

/**
 * Centros de custo no Financeiro (04/11/2026): o mesmo cadastro da aba do DP
 * (centros, lojas e grupos da DRE), pelas rotas `/financeiro/settings/*`.
 */
export default function CostCentersSettings({ token, canWrite }: Props) {
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [groups, setGroups] = useState<DpCostCenterGroup[]>([]);
  const [stores, setStores] = useState<FinStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<DpCostCenter | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, g] = await Promise.all([listCostCentersApi(token, true, BASE), listCostCenterGroupsApi(token, true, BASE)]);
      setCostCenters(c);
      setGroups(g);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os centros de custo.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    load();
    listStoresApi(token)
      .then(setStores)
      .catch(() => setStores([]));
  }, [token, load]);

  const open = (c?: DpCostCenter) => {
    setForm(
      c
        ? { code: c.code, name: c.name, storeIds: c.storeIds ?? [], isActive: c.isActive, groupId: c.groupId ?? "" }
        : emptyForm
    );
    setModal({ id: c?.id });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Informe o código e o nome.");
      return;
    }
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      storeIds: form.storeIds,
      isActive: form.isActive,
      groupId: form.groupId || null,
    };
    setSaving(true);
    try {
      if (modal.id) await updateCostCenterApi(token, modal.id, payload, BASE);
      else await createCostCenterApi(token, payload, BASE);
      toast.success("Centro de custo salvo.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar centro de custo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteCostCenterApi(token, removing.id, BASE);
      toast.success("Centro de custo excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir centro de custo.");
    } finally {
      setRemoving(null);
      setSaving(false);
    }
  };

  if (loading) return <p className="dpEmpty">Carregando…</p>;

  return (
    <>
      {canWrite ? (
        <div className="dpControls">
          <button className="dpBtn dpBtn--primary" onClick={() => open()}>
            Novo centro de custo
          </button>
        </div>
      ) : null}

      <CostCenterGroupsSettings token={token} groups={groups} onChanged={load} apiBase={BASE} canWrite={canWrite} stores={stores} />

      <p className="dpNotice">
        Um centro de custo pode agrupar várias lojas. O vínculo é opcional: há centro de custo que não corresponde a
        loja nenhuma, como o administrativo. Este cadastro é o mesmo da aba do DP.
      </p>

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Grupo</th>
              <th>Lojas</th>
              <th>Situação</th>
              {canWrite ? <th style={{ width: 160 }}>Ações</th> : null}
            </tr>
          </thead>
          <tbody>
            {costCenters.length === 0 ? (
              <tr>
                <td colSpan={canWrite ? 6 : 5} className="dpEmpty">
                  Nenhum centro de custo cadastrado.
                </td>
              </tr>
            ) : (
              costCenters.map((c) => (
                <tr key={c.id}>
                  <td>{c.code}</td>
                  <td>{c.name}</td>
                  <td>
                    {c.group ? `${c.group.code} — ${[c.group.parent?.name, c.group.name].filter(Boolean).join(" › ")}` : "—"}
                  </td>
                  <td>{c.stores.map((s) => s.nomeFantasia).join(", ") || "—"}</td>
                  <td>
                    <span className={`dpBadge ${c.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                      {c.isActive ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  {canWrite ? (
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(c)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoving(c)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Centro de custo">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.id ? "Editar centro de custo" : "Novo centro de custo"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)} disabled={saving} aria-label="Fechar">
                &times;
              </button>
            </header>

            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="finCcCode">Código *</label>
                  <input
                    id="finCcCode"
                    className="dpInput"
                    value={form.code}
                    maxLength={20}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    disabled={saving}
                  />
                  <span className="dpFormHint">Gravado em maiúsculas.</span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="finCcName">Nome *</label>
                  <input
                    id="finCcName"
                    className="dpInput"
                    value={form.name}
                    maxLength={120}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="finCcGroup">Grupo</label>
                  <select
                    id="finCcGroup"
                    className="dpSelect"
                    value={form.groupId}
                    onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Sem grupo</option>
                    {groups.map((g) => (
                      <optgroup key={g.id} label={`${g.code} — ${g.name}`}>
                        <option value={g.id} disabled={!g.isActive && g.id !== form.groupId}>
                          {g.code} — {g.name}
                          {g.isActive ? "" : " (inativo)"}
                        </option>
                        {g.children.map((sub) => (
                          <option key={sub.id} value={sub.id} disabled={!sub.isActive && sub.id !== form.groupId}>
                            {sub.code} — {g.name} › {sub.name}
                            {sub.isActive ? "" : " (inativo)"}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <span className="dpFormHint">Onde o centro aparece na DRE.</span>
                </div>

                <fieldset className="dpFieldset">
                  <legend>Lojas</legend>
                  {stores.length === 0 ? (
                    <span className="dpFormHint">Nenhuma loja cadastrada.</span>
                  ) : (
                    <div className="dpCheckboxRow">
                      {stores.map((s) => (
                        <label key={s.id}>
                          <input
                            type="checkbox"
                            checked={form.storeIds.includes(s.id)}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                storeIds: e.target.checked ? [...f.storeIds, s.id] : f.storeIds.filter((id) => id !== s.id),
                              }))
                            }
                            disabled={saving}
                          />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  )}
                  <span className="dpFormHint">
                    Marque todas as lojas que este centro de custo agrupa. Deixe em branco se não corresponder a loja
                    nenhuma.
                  </span>
                </fieldset>

                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>
              </div>

              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir centro de custo?"
        message={
          removing
            ? `"${removing.name}" será excluído. Centro de custo já usado em contrato não se exclui: nesse caso, inative.`
            : ""
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
